import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation } from 'next-i18next'
// Libs
import styled, { css } from 'styled-components'

// Comps

import { useContext, useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'

import { Page } from '../components/Page'
import { Section } from '../components/Section'
import { HowItWork } from '../components/HowItWork'
import { H2, P } from '../components/Headings'
import { CreateEventCTABanner } from '../components/CreateEventCTABanner'
import { EventsContext, UserContext } from '../context'
import { NookEvent } from '../types'
import { ExploreAll } from '../components/ExploreAll'
import { BowedHeroSection } from '../components/Sections/BowedHeroSection'
import { show } from '../utils'

// Styles
import { aboutPageStyles } from '../components/pageStyles/aboutPageStyles'

const PageContainer = styled(Page)`
  ${aboutPageStyles}
`

// Props.
interface Props {}

// A
const AboutPage: React.FC<Props> = (props) => {
  const { t } = useTranslation('common')

  const hiwItems = useMemo(() => {
    return [
      {
        title: t(
          `about_20220310172945333216557`,
          'Sign up and create your Klyk for free',
        ),
        content: t(
          `about_20220310172950013262068`,
          'Create your own page to organise, share and promote your interest group or event.',
        ),
      },
      {
        title: t(`about_20220310172954433181510`, 'Everyone on the same page'),
        content: t(
          `about_20220310172958237223153`,
          'Keep the details all in one place and have your guests receive reminders and updates in the lead up.',
        ),
      },
      {
        title: t(
          `about_20220310173002189337026`,
          'Join your event in an instant',
        ),
        content: t(
          `about_20220310173006417330226`,
          'Make it easy to access the video call and participate in the online experience.',
        ),
      },
    ]
  }, [t])

  const router = useRouter()
  const { events, fetchEvents, resetCurrentEventPage, fetchPastEvents } =
    useContext(EventsContext)
  const { currentUser } = useContext(UserContext)
  const [eventsToShow, setEventsToShow] = useState<NookEvent[]>([])

  const [pastEvents, setPastEvents] = useState<NookEvent[]>(undefined)

  useEffect(() => {
    setEventsToShow(events)
  }, [events])

  useEffect(() => {
    // if (!events || events.length < 4) {
    fetchEvents({ category: 'allCategory', recommended: true })
    // } else { setEventsToShow(events); }
  }, [])

  useEffect(() => {
    fetchPastEvents({}).then((pastEventsResponse) => {
      setPastEvents(pastEventsResponse.results)
    })
  }, [fetchPastEvents])

  useEffect(() => {
    if (currentUser === null) {
      resetCurrentEventPage(true)
    }
  }, [currentUser])

  // ..
  return (
    <PageContainer>
      <BowedHeroSection
        bgImage={'/images/aboutHero.jpg'}
        smallHeader={t(`about_20220310172408587664637`, 'ABOUT')}
        largeHeader={t(`about1848444710`, `Together, even when apart`)}
        showCTA={false}
      />

      {/* Quote Section. */}
      <Section className="centerSection quoteSection">
        <P className="quoteText bodyQuote">
          {t(
            `about-906370140`,
            `Klyk is a platform for finding and building communities and groups that foster social connection. People love sharing experiences and Klyk provides an easy way to do this digitally.`,
          )}
        </P>
      </Section>

      {/* About Section. */}
      <Section className="centerSection aboutSection">
        <P className="quoteText quoteTextLeft">
          {t(
            `about-308415762`,
            `Take your usual social gatherings online, or register for an event or activity where you might learn something novel or meet someone new. Klyk helps people get out of their comfort zone and create communities around the things they love most.`,
          )}
        </P>
      </Section>

      {/* How it works Section. */}
      <Section className="howItWorksSection">
        <H2 className="quoteText">{t(`How it works`, `How it works`)}</H2>
        <HowItWork items={hiwItems} />
      </Section>

      {/* Create event Section. */}
      <Section className="createEventSection">
        <div className="sectionContainer">
          <CreateEventCTABanner
            bannerHeader={t(
              `about1189263620`,
              `Join today for free to start hosting your own events as an individual, or business.`,
            )}
            bannerContent={t(
              `about-66212686`,
              `See how others are using Klyk to get together, even when apart.`,
            )}
            showCreateEventButton={true}
          />
        </div>
      </Section>

      {/* Take a look Section. */}
      {show(events && events.length > 0) && (
        <>
          <ExploreAll
            header={t(`Take a look`, `Take a look`)}
            className="sectionContainer"
            page={'About'}
            events={events}
            showExploreAllCta={false}
          />
        </>
      )}
      {show(pastEvents && pastEvents.length > 0) && (
        <>
          <ExploreAll
            header={t(`Past events`, `Past events`)}
            className="sectionContainer"
            page="About"
            events={pastEvents}
            showExploreAllCta={false}
          />
        </>
      )}
    </PageContainer>
  )
}

// Export.
export default AboutPage
export async function getStaticProps({ locale }) {
  const translations = await serverSideTranslations(locale, ['common'])
  return {
    props: {
      ...translations,
    },
  }
}
